import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Read } from 'services/cobranca';
import { PerfilContent, StatusContent, Title } from 'ui/styled';
import { marcadorDiferente, normalizeResponse, selectColors } from 'utils';
import { parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function BankingCobranca() {
  const { user, filterExpression, setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  const [tick, setTick] = useState(0);

  // Atualiza a cada 2 minutos
  useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 120000);
    return () => clearInterval(interval);
  }, []);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Read(
        '?filters[titulos][status]=vencido&filters[titulos][dias_vencido][$gte]=1&filters[$not][status]=renegociar'
      );
      const arrayResult = normalizeResponse(result);
      //traz o array dos titulos separado para trazer cada um em uma linha.
      const data = [];
      arrayResult.forEach((item) => {
        item.titulos.forEach((titulo) => {
          if (titulo.status === 'vencido') {
            data.push({ item: item, titulos: titulo });
          }
        });
        return data;
      });

      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => {
        const dataA = new Date(a.titulos?.data_vencimento || 0);
        const dataB = new Date(b.titulos?.data_vencimento || 0);
        return dataA - dataB;
      });
      const next = sorted.map((item) => ({ ...item }));
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        ref: 'id',
        renderCell: ({ row }) => {
          //verifica se ja teve renegociacao.
          const origemRenegociado = row?.parcelas_origem;
          const renegociado = row?.titulos
            .filter((a) => a.status === 'baixado')
            .map((a) => a.renegociar)
            .reduce((verdadeiro, renegociado) => {
              return renegociado === true ? true : false;
            }, null);
          // Pega o ID
          const cobrancaId = row?.id;
          if (origemRenegociado && renegociado) {
            return marcadorDiferente(cobrancaId, '! #');
          } else if (origemRenegociado) {
            return marcadorDiferente(cobrancaId, '!');
          } else if (renegociado) {
            return marcadorDiferente(cobrancaId, '#');
          }
          return cobrancaId;
        },
      },
      {
        title: 'Data',
        ref: 'date',
        sortable: true,
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.date),
      },
      { title: 'Banco', ref: 'banco' },
      // Inserir coluna "Distribuidora" condicionalmente
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [
            {
              title: 'Distribuidora',
              sortable: true,
              ref: 'distributor',
            },
          ]
        : []),
      { title: 'Integrador', ref: 'integrator' },
      { title: 'Cliente', ref: 'customer', sortable: true },
      {
        title: 'Telefone',
        ref: 'telefone',
        renderCell: ({ row }) => {
          if (!row?.telefone) return '-';
          const numeroLimpo = row.telefone.replace(/\D/g, '');
          const linkWhats = `https://wa.me/55${numeroLimpo}`;
          return (
            <a
              href={linkWhats}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'blue',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {row.telefone}
            </a>
          );
        },
      },
      {
        title: 'Perfil',
        ref: 'bureau_perfil',
        align: 'center',
        renderCell: ({ row }) => (
          <PerfilContent color={selectColors(row?.bureau_perfil)}>
            {row?.bureau_perfil}
          </PerfilContent>
        ),
      },
      { title: 'Taxa', ref: 'taxa' },
      { title: 'Valor Contrato', ref: 'value' },
      { title: 'Parcela', ref: 'parcela' },
      { title: 'PMT', ref: 'valor_parcelas' },
      {
        title: 'Vencimento',
        ref: 'data_vencimento',
        sortable: true,
        renderCell: ({ row }) =>
          formatadores.TIMESTAMP_TO_BR(row.data_vencimento),
      },
      { title: 'Dias em Atraso', ref: 'dias_atraso', sortable: true },
      { title: 'PMT (capital + juros)', ref: 'pmt_total' },
      {
        title: 'TAG',
        ref: 'renegociar',
        renderCell: ({ row }) => {
          return (
            <StatusContent
              color={selectColors(row?.status)}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setModal({
                  type: 'cobranca',
                  dados: row,
                  reload: () => init(),
                });
              }}
            >
              Renegociar
            </StatusContent>
          );
        },
      },
    ];
    const insertIndex = 3;
    if (
      user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
    ) {
      baseColumns.splice(insertIndex, 0, {
        title: 'Distribuidora',
        ref: 'distributor',
      });
    }
    return baseColumns;
  }, [user, init, setModal]);
  const rows = useMemo(() => {
    return (registers || []).map((a) => ({
      id: a?.item.id,
      date: a?.item.createdAt,
      formatedDate: formatadores.TIMESTAMP_TO_BR(a?.item.createdAt),
      cliente: a?.item.cliente,
      distributor: normalizeName(a?.item.distribuidor?.trade_name) || '',
      distributorId: a?.item.distribuidor?.id,
      integrator:
        normalizeName(a?.item.proposta_financeira?.integrador?.nome_fantasia) ||
        '',
      customer: normalizeName(a?.item.cliente?.nome_completo) || '',
      value: parseCurrency(a?.item.valor_financiado),
      bureau_perfil: !a?.item.banking
        ? '-'
        : !a?.item.banking.bureau_perfil
          ? '-'
          : a?.item.banking.bureau_perfil,
      taxa: `${a?.item.perc_juros}%`,
      banco: normalizeName(a?.item.banco?.nome_fantasia),
      telefone: a?.item.cliente?.telefone_celular,
      valor_parcelas: parseCurrency(a?.titulos.valor),
      parcela: a?.titulos.parcela,
      data_vencimento: a?.titulos.data_vencimento,
      dias_atraso: a?.titulos.dias_vencido,
      pmt_total: parseCurrency(
        a?.titulos.valor_juros_remun +
          a?.titulos.capital +
          a?.titulos.valor_juros_mora +
          a?.titulos.valor_multa +
          a?.titulos.valor_iof_juros
      ),
      parcelas_origem: a?.item.parcelas_origem,
      renegociacao_taxa: a?.item.renegociacao_taxa,
      titulos: a?.item?.titulos,
      status: a?.item.status,
    }));
  }, [registers]);

  useEffect(() => init(), [init, tick]);

  return (
    <ContainerAuthenticated>
      <Title>Banking</Title>
      <TableWrapper title="Cobrança" search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
        ! cobrança criada a partir de uma renegociação. <br /># cobrança que ja
        foram renegociadas.
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
